import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import { trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import type { DateRangeQueryOptions } from '../../utils/visibility-range';
import type { PromptSourceInsightsResult } from '../../types';
import { promptSourcesColumnConfig } from '../../config/promptSourcesColumnConfig';
import DataLoadError from '@/components/errors/DataLoadError';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';

type AgGridWrapperProps = {
  table_name?: string;
  height?: string;
  columns: unknown[];
  data: unknown[];
  total?: number;
  otherOptions?: Record<string, unknown>;
  settings?: Record<string, unknown>;
};
const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

export function PromptSourcesPanel({
  promptId,
  rangeQuery,
}: {
  promptId: string;
  rangeQuery: DateRangeQueryOptions;
}) {
  const [data, setData] = useState<PromptSourceInsightsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  const queryKey = useMemo(() => JSON.stringify(rangeQuery), [rangeQuery]);
  const columns = useMemo(() => promptSourcesColumnConfig(), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    api
      .getPromptSourceInsights(promptId, rangeQuery)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [promptId, queryKey, rangeQuery, retryNonce]);

  if (loading && !error) {
    return <AgGridSkeleton height="360px" />;
  }

  return (
    <Card className="overflow-hidden ag-grid-in-card">
      <FalconCardHeader
        light
        titleTag="h6"
        className="py-3"
        title={
          <div>
            منابع استفاده‌شده در پاسخ AI
            <p className="fs--2 text-700 mb-0 fw-normal mt-2">
              آدرس‌ها و دامنه‌هایی که در پاسخ‌های AI این پرامپت شناسایی شده‌اند
            </p>
          </div>
        }
      />

      {error ? (
        <div className="px-3 py-4">
          <DataLoadError
            compact
            onRetry={() => setRetryNonce(n => n + 1)}
            isRetrying={loading}
          />
        </div>
      ) : !data || data.items.length === 0 ? (
        <Card.Body className="d-flex align-items-center justify-content-center px-3 py-5">
          <div className="text-center" style={{ maxWidth: '32rem' }}>
            <span
              className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary text-white mb-3"
              style={{ width: '3.25rem', height: '3.25rem' }}
              aria-hidden
            >
              <FontAwesomeIcon icon="link" className="fs-1 text-white" />
            </span>
            <h3 className="fs-0 fw-semibold text-800 mb-2">
              هنوز منبعی برای این پرامپت پیدا نشده است
            </h3>
            <p className="fs--1 text-600 mb-3">
              در پاسخ‌های ثبت‌شده برای این پرامپت، هنوز آدرس یا Citation قابل شناسایی وجود ندارد.
            </p>
            <div className="d-inline-flex align-items-start gap-2 text-start rounded-3 border border-200 bg-body-tertiary px-3 py-2">
              <FontAwesomeIcon icon="info-circle" className="text-500 mt-1 flex-shrink-0" />
              <span className="fs--2 text-500">
                اگر پاسخ مدل شامل آدرس یا Citation باشد، موارد شناسایی‌شده در همین بخش نمایش داده
                می‌شوند.
              </span>
            </div>
          </div>
        </Card.Body>
      ) : (
        <TypedAgGridWrapper
          table_name="tracker_ai_prompt_sources"
          height="360px"
          columns={columns}
          data={data.items}
          total={data.items.length}
          otherOptions={{ rowHeight: 48 }}
          settings={{ pagination: true, fullscreen: false, sideBar: false, export: { enabled: false } }}
        />
      )}
    </Card>
  );
}
